import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { oauthAPI } from '../services/api';
import { toast } from 'react-toastify';
import {
  CheckCircleIcon,
  ArrowRightIcon,
  CloudArrowUpIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import VaultoLogo from './VaultoLogo';

const OnboardingWizard = ({ 
  oauthStatus, 
  onComplete, 
  onSkip,
  syncLoading,
  onStartSync 
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [showAccountTypeModal, setShowAccountTypeModal] = useState(false);
  const [showGoogleInstructions, setShowGoogleInstructions] = useState(false);
  const [showPersonalInfoModal, setShowPersonalInfoModal] = useState(false);
  const [showInstagramExplanationModal, setShowInstagramExplanationModal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [backupStarted, setBackupStarted] = useState(false);

  // Lock body scroll when wizard is mounted
  useEffect(() => {
    // Save original styles
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    const originalHeight = document.body.style.height;
    
    // Lock scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
      document.body.style.height = originalHeight;
    };
  }, []);

  // Determine current step based on connection status (Step 1 = Google Drive, Step 2 = Instagram)
  useEffect(() => {
    if (oauthStatus) {
      if (!oauthStatus.google_connected) {
        setCurrentStep(1);
      } else if (!oauthStatus.instagram_connected) {
        setCurrentStep(2);
      } else {
        setCurrentStep(3);
      }
    }
  }, [oauthStatus]);

  // Auto-advance animation when connections complete (Step 1 = Google Drive)
  useEffect(() => {
    if (oauthStatus?.google_connected && currentStep === 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(2);
        setIsAnimating(false);
      }, 800);
    }
  }, [oauthStatus?.google_connected, currentStep]);

  // Auto-advance animation when Instagram connects (Step 2 = Instagram)
  useEffect(() => {
    if (oauthStatus?.instagram_connected && currentStep === 2) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(3);
        setIsAnimating(false);
      }, 800);
    }
  }, [oauthStatus?.instagram_connected, currentStep]);

  const handleConnectInstagramClick = () => {
    setShowAccountTypeModal(true);
  };

  const handleConnectInstagram = async (accountType = 'business') => {
    setShowAccountTypeModal(false);
    // Show explanation modal first, store accountType for later
    sessionStorage.setItem('pending_instagram_account_type', accountType);
    setShowInstagramExplanationModal(true);
  };

  // Proceed with Instagram OAuth after user confirms
  const proceedWithInstagramOAuth = async () => {
    setShowInstagramExplanationModal(false);
    const accountType = sessionStorage.getItem('pending_instagram_account_type') || 'business';
    sessionStorage.removeItem('pending_instagram_account_type');
    // Store wizard mode flag for OAuth redirect
    sessionStorage.setItem('wizard_mode_active', 'true');
    // Store connection attempt for abandoned flow recovery
    localStorage.setItem('instagram_connection_attempted', Date.now().toString());
    try {
      const response = await oauthAPI.getInstagramAuthUrl(accountType);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Handle "I have a personal account" - show info modal about switching to Creator
  const handlePersonalAccountHelp = async () => {
    setShowAccountTypeModal(false);
    // Log this click for analytics
    try {
      await oauthAPI.logPersonalAccountClick();
    } catch (e) {
      // Ignore errors - logging shouldn't block
    }
    setShowPersonalInfoModal(true);
  };

  // Proceed to Instagram OAuth after user acknowledges they need to switch to Creator
  const handlePersonalAccountContinue = () => {
    setShowPersonalInfoModal(false);
    handleConnectInstagram('business'); // Uses same OAuth flow
  };

  // Simple exit confirmation for wizard modals
  const handleExitConfirm = (closeAction) => {
    const confirmed = window.confirm(t('wizard.exitConfirm.message'));
    if (confirmed) {
      closeAction();
    }
  };

  const handleConnectGoogle = () => {
    setShowGoogleInstructions(true);
  };

  const proceedWithGoogleConnection = async () => {
    setShowGoogleInstructions(false);
    // Store wizard mode flag for OAuth redirect
    sessionStorage.setItem('wizard_mode_active', 'true');
    try {
      const response = await oauthAPI.getGoogleAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleStartBackup = async () => {
    setBackupStarted(true);
    await onStartSync();
    // Mark wizard as complete
    sessionStorage.removeItem('wizard_mode_active');
    localStorage.setItem('wizard_completed', 'true');
    // Call onComplete after a short delay to show success state
    setTimeout(() => {
      onComplete();
    }, 2000);
  };

  // Steps array - Step 1 = Google Drive, Step 2 = Instagram
  const steps = [
    {
      number: 1,
      title: t('wizard.step1.title'),
      shortTitle: t('wizard.step1.shortTitle'),
      completed: oauthStatus?.google_connected,
    },
    {
      number: 2,
      title: t('wizard.step2.title'),
      shortTitle: t('wizard.step2.shortTitle'),
      completed: oauthStatus?.instagram_connected,
    },
    {
      number: 3,
      title: t('wizard.step3.title'),
      shortTitle: t('wizard.step3.shortTitle'),
      completed: backupStarted,
    },
  ];

  const progressPercentage = ((currentStep - 1) / 3) * 100 + (steps[currentStep - 1]?.completed ? 33.33 : 0);

  return (
    <>
      {/* Full-screen overlay - use dvh for mobile browser chrome, prevent touch scrolling */}
      <div 
        className="fixed inset-0 z-[100] overflow-hidden bg-gradient-to-br from-indigo-900/95 via-purple-900/95 to-pink-800/95 backdrop-blur-xl"
        style={{ height: '100dvh', touchAction: 'none' }}
      >
        {/* Background decorative elements - hidden on mobile for performance */}
        <div className="absolute inset-0 overflow-hidden hidden sm:block">
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-32 right-10 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-10 left-1/3 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        {/* Main content container - flex column to handle safe areas */}
        <div className="relative h-full flex flex-col">
          {/* Header with safe area padding for mobile browser chrome */}
          <div className="flex-shrink-0 pt-4 pb-2 sm:pt-6 sm:pb-4 px-4 sm:px-6" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 16px))' }}>
            <div className="flex items-center justify-between">
              <VaultoLogo className="h-8 sm:h-10" />
              {onSkip && (
                <button
                  onClick={onSkip}
                  className="text-xs sm:text-sm text-white/70 hover:text-white transition-colors"
                >
                  {t('wizard.skipToDashboard')}
                </button>
              )}
            </div>
          </div>

          {/* Scrollable content area - only scrolls if content overflows */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 pb-4 sm:pb-8">
            <div className="flex flex-col items-center justify-center min-h-full py-2 sm:py-4">
          
          {/* Progress Steps */}
          <div className="w-full max-w-2xl mb-4 sm:mb-8">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div 
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-base sm:text-lg font-bold transition-all duration-500 ${
                        step.completed 
                          ? 'bg-green-500 text-white shadow-lg shadow-green-500/30' 
                          : currentStep === step.number
                            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/30 ring-2 sm:ring-4 ring-pink-500/30'
                            : 'bg-white/20 text-white/60'
                      }`}
                    >
                      {step.completed ? (
                        <CheckCircleSolidIcon className="h-5 w-5 sm:h-7 sm:w-7" />
                      ) : (
                        step.number
                      )}
                    </div>
                    <span className={`mt-1 sm:mt-2 text-xs sm:text-sm font-medium hidden sm:block ${
                      currentStep === step.number ? 'text-white' : 'text-white/60'
                    }`}>
                      {step.shortTitle}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-2 sm:mx-4 h-0.5 sm:h-1 rounded-full overflow-hidden bg-white/10">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-700"
                        style={{ width: step.completed ? '100%' : '0%' }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-white/10 rounded-full h-1.5 sm:h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-green-500 transition-all duration-700 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <p className="text-center text-xs sm:text-sm text-white/80 mt-1 sm:mt-2">
              {Math.round(progressPercentage)}% {t('wizard.complete')}
            </p>
          </div>

          {/* Wizard Card */}
          <div className={`w-full max-w-xl transition-all duration-500 ${isAnimating ? 'scale-95 opacity-50' : 'scale-100 opacity-100'}`}>
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-5 sm:p-8 border border-white/20 shadow-2xl">
              
              {/* Step 1: Connect Google Drive (swapped - now first) */}
              {currentStep === 1 && (
                <div className="text-center">
                  <div className="mb-4 sm:mb-6">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-gradient-to-br from-blue-500 via-green-500 to-yellow-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl shadow-blue-500/30 mb-3 sm:mb-4">
                      <CloudArrowUpIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    </div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">
                      {t('wizard.step1.title')}
                    </h2>
                    <p className="text-white/90 text-sm sm:text-base md:text-lg">
                      {t('wizard.step1.description')}
                    </p>
                  </div>

                  <div className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-white/10">
                    <div className="flex items-start space-x-3 text-left">
                      <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-medium text-sm sm:text-base">{t('wizard.step1.benefit1')}</p>
                        <p className="text-white/70 text-xs sm:text-sm mt-1">{t('wizard.step1.benefit1Desc')}</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleConnectGoogle}
                    className="w-full py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-r from-blue-500 via-green-500 to-yellow-500 text-white font-semibold rounded-xl sm:rounded-2xl hover:from-blue-600 hover:via-green-600 hover:to-yellow-600 focus:outline-none focus:ring-4 focus:ring-blue-500/30 shadow-lg shadow-blue-500/30 transform hover:scale-[1.02] transition-all duration-200 text-base sm:text-lg"
                  >
                    {t('wizard.step1.button')}
                    <ArrowRightIcon className="inline-block h-4 w-4 sm:h-5 sm:w-5 ml-2" />
                  </button>

                  <p className="text-white/70 text-xs sm:text-sm mt-3 sm:mt-4">
                    {t('wizard.step1.note')}
                  </p>
                </div>
              )}

              {/* Step 2: Connect Instagram (swapped - now second) */}
              {currentStep === 2 && (
                <div className="text-center">
                  <div className="mb-4 sm:mb-6">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl shadow-pink-500/30 mb-3 sm:mb-4">
                      <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">
                      {t('wizard.step2.title')}
                    </h2>
                    <p className="text-white/90 text-sm sm:text-base md:text-lg">
                      {t('wizard.step2.description')}
                    </p>
                  </div>

                  <div className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-white/10">
                    <div className="flex items-start space-x-3 text-left">
                      <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-medium text-sm sm:text-base">{t('wizard.step2.benefit1')}</p>
                        <p className="text-white/70 text-xs sm:text-sm mt-1">{t('wizard.step2.benefit1Desc')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Urgency Message */}
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-2.5 mb-4">
                    <div className="flex items-center justify-center space-x-2">
                      <ExclamationTriangleIcon className="h-4 w-4 text-orange-400 flex-shrink-0" />
                      <span className="text-orange-300 text-xs font-medium">{t('wizard.step2.urgency')}</span>
                    </div>
                  </div>

                  {/* Google Drive Connected Badge */}
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg sm:rounded-xl p-2 sm:p-3 mb-4 sm:mb-6 inline-flex items-center space-x-2">
                    <CheckCircleSolidIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                    <span className="text-green-300 text-xs sm:text-sm font-medium">{t('wizard.step2.googleConnected')}</span>
                  </div>

                  <button
                    onClick={handleConnectInstagramClick}
                    className="w-full py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-r from-pink-500 via-purple-500 to-orange-500 text-white font-semibold rounded-xl sm:rounded-2xl hover:from-pink-600 hover:via-purple-600 hover:to-orange-600 focus:outline-none focus:ring-4 focus:ring-pink-500/30 shadow-lg shadow-pink-500/30 transform hover:scale-[1.02] transition-all duration-200 text-base sm:text-lg"
                  >
                    {t('wizard.step2.button')}
                    <ArrowRightIcon className="inline-block h-4 w-4 sm:h-5 sm:w-5 ml-2" />
                  </button>

                  <p className="text-white/70 text-xs sm:text-sm mt-3 sm:mt-4">
                    {t('wizard.step2.note')}
                  </p>
                </div>
              )}

              {/* Step 3: Start Backup */}
              {currentStep === 3 && (
                <div className="text-center">
                  <div className="mb-4 sm:mb-6">
                    {!backupStarted ? (
                      <>
                        <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl shadow-green-500/30 mb-3 sm:mb-4 animate-pulse">
                          <PlayIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                        </div>
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">
                          {t('wizard.step3.title')}
                        </h2>
                        <p className="text-white/90 text-sm sm:text-base md:text-lg">
                          {t('wizard.step3.description')}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl shadow-green-500/30 mb-3 sm:mb-4">
                          <CheckCircleSolidIcon className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                        </div>
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">
                          {t('wizard.step3.successTitle')}
                        </h2>
                        <p className="text-white/90 text-sm sm:text-base md:text-lg">
                          {t('wizard.step3.successDescription')}
                        </p>
                      </>
                    )}
                  </div>

                  {/* Connected Services */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4">
                      <CheckCircleSolidIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-400 mx-auto mb-1 sm:mb-2" />
                      <p className="text-green-300 text-xs sm:text-sm font-medium">Instagram</p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4">
                      <CheckCircleSolidIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-400 mx-auto mb-1 sm:mb-2" />
                      <p className="text-green-300 text-xs sm:text-sm font-medium">Google Drive</p>
                    </div>
                  </div>

                  {!backupStarted ? (
                    <>
                      <div className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-white/10">
                        <p className="text-white font-medium text-sm sm:text-base mb-1 sm:mb-2">{t('wizard.step3.readyMessage')}</p>
                        <p className="text-white/70 text-xs sm:text-sm">{t('wizard.step3.readyDescription')}</p>
                      </div>

                      <button
                        onClick={handleStartBackup}
                        disabled={syncLoading}
                        className="w-full py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl sm:rounded-2xl hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-4 focus:ring-green-500/30 shadow-lg shadow-green-500/30 transform hover:scale-[1.02] transition-all duration-200 text-base sm:text-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {syncLoading ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t('wizard.step3.backingUp')}
                          </span>
                        ) : (
                          <>
                            {t('wizard.step3.button')}
                            <PlayIcon className="inline-block h-4 w-4 sm:h-5 sm:w-5 ml-2" />
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4">
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-green-400" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-green-300 text-sm sm:text-base font-medium">{t('wizard.step3.redirecting')}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Type Selection Modal */}
      {showAccountTypeModal && (
        <div className="fixed inset-0 z-[110] overflow-hidden flex items-end sm:items-center justify-center" style={{ touchAction: 'none' }}>
          <div className="fixed inset-0 transition-opacity bg-black/80 backdrop-blur-sm" onClick={() => handleExitConfirm(() => setShowAccountTypeModal(false))} />
            
          <div className="relative w-full max-w-md p-4 sm:p-6 overflow-hidden text-left transition-all transform bg-gray-900/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-t-2xl sm:rounded-2xl mx-0 sm:mx-4">
            <div className="text-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">
                {t('wizard.accountType.title')}
              </h3>
              <p className="text-xs sm:text-sm text-white/80">
                {t('wizard.accountType.description')}
              </p>
              {/* Official API Badge */}
              <div className="flex justify-center mt-3">
                <div className="flex items-center px-3 py-1.5 bg-green-500/10 rounded-full border border-green-500/30">
                  <ShieldCheckIcon className="h-4 w-4 text-green-400 mr-2" />
                  <span className="text-xs text-green-300 font-medium">Official Instagram API</span>
                </div>
              </div>
            </div>

            {/* Loss aversion warning */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
              <div className="flex items-start space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-200 text-xs">
                  Every day, creators lose content to hacks, suspensions, and accidental deletions. Protect your work now.
                </p>
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <button
                onClick={() => handleConnectInstagram('business')}
                className="w-full flex items-center p-3 sm:p-4 bg-gradient-to-r from-pink-500/20 to-purple-600/20 hover:from-pink-500/30 hover:to-purple-600/30 border border-pink-500/30 rounded-xl transition-all"
              >
                <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="ml-3 sm:ml-4 text-left">
                  <p className="font-semibold text-white text-sm sm:text-base">Creator or Business Account</p>
                  <p className="text-xs text-white/70">{t('wizard.accountType.businessDesc')}</p>
                </div>
              </button>

              <button
                onClick={handlePersonalAccountHelp}
                className="w-full flex items-center p-3 sm:p-4 bg-white/5 hover:bg-white/10 border border-white/20 rounded-xl transition-all"
              >
                <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 bg-gray-600 rounded-lg flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-400" />
                </div>
                <div className="ml-3 sm:ml-4 text-left flex-1">
                  <p className="font-semibold text-white text-sm sm:text-base">{t('wizard.accountType.personal')}</p>
                  <p className="text-xs text-white/70">I'll switch to Creator (it's free!)</p>
                </div>
              </button>

              <button
                onClick={() => handleExitConfirm(() => setShowAccountTypeModal(false))}
                className="w-full p-2 sm:p-3 text-sm text-white/60 hover:text-white transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Personal Account Info Modal - Switch to Creator */}
      {showPersonalInfoModal && (
        <div className="fixed inset-0 z-[110] overflow-hidden flex items-end sm:items-center justify-center" style={{ touchAction: 'none' }}>
          <div className="fixed inset-0 transition-opacity bg-black/80 backdrop-blur-sm" onClick={() => setShowPersonalInfoModal(false)} />
          
          <div className="relative w-full max-w-lg p-4 sm:p-6 overflow-hidden text-left transition-all transform bg-gray-900/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-t-2xl sm:rounded-2xl mx-0 sm:mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowPersonalInfoModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            
            <div className="text-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">
                Join Millions of Creators
              </h3>
              <p className="text-xs sm:text-sm text-white/80">
                Top influencers and serious content creators use Creator accounts for powerful insights and tools. Upgrade in 30 seconds - it's completely free!
              </p>
            </div>

            {/* Step-by-step guide with screenshots */}
            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
              {/* Step 1 */}
              <div className="bg-white/5 rounded-xl p-3 sm:p-4 border border-white/10">
                <p className="text-xs sm:text-sm font-medium text-white mb-2 sm:mb-3">
                  <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-pink-500 text-white text-xs font-bold mr-2">1</span>
                  Instagram will ask you to switch to Professional
                </p>
                <img 
                  src="/instagram_switch_to_professional.jpeg" 
                  alt="Instagram switch to professional prompt" 
                  className="w-full max-w-[200px] sm:max-w-xs mx-auto rounded-lg border border-white/20 shadow-lg"
                />
              </div>

              {/* Step 2 */}
              <div className="bg-white/5 rounded-xl p-3 sm:p-4 border border-white/10">
                <p className="text-xs sm:text-sm font-medium text-white mb-2 sm:mb-3">
                  <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-pink-500 text-white text-xs font-bold mr-2">2</span>
                  Select "Creator" when asked
                </p>
                <div className="relative w-full max-w-[200px] sm:max-w-xs mx-auto">
                  <img 
                    src="/instagram_creator_or_business.jpeg" 
                    alt="Instagram creator or business selection" 
                    className="w-full rounded-lg border border-white/20 shadow-lg"
                  />
                  {/* Green indicator circle on Creator option */}
                  <div className="absolute top-[28%] right-[8%] w-6 h-6 sm:w-8 sm:h-8 border-3 sm:border-4 border-green-400 rounded-full animate-pulse"></div>
                  <div className="absolute top-[28%] right-[8%] w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">
                    <CheckCircleIcon className="h-4 w-4 sm:h-6 sm:w-6 text-green-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Info text */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-blue-200">
                <strong>Why Creators choose this:</strong> Access to analytics and insights, professional tools, and the ability to automatically backup your content with Vaulto. Plus, you can switch back anytime - your followers and content stay exactly the same.
              </p>
            </div>

            {/* Action buttons */}
            <div className="space-y-2 sm:space-y-3">
              <button
                onClick={handlePersonalAccountContinue}
                className="w-full flex items-center justify-center px-4 py-2.5 sm:py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all text-sm sm:text-base"
              >
                Continue to Instagram
              </button>

              <button
                onClick={() => setShowPersonalInfoModal(false)}
                className="w-full px-4 py-2 sm:py-3 text-xs sm:text-sm text-white/60 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Drive Instructions Modal */}
      {showGoogleInstructions && (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm" style={{ touchAction: 'none' }}>
          <div className="bg-gray-900/95 backdrop-blur-xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-8 border border-white/20 mx-0 sm:mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">{t('wizard.googleInstructions.title')}</h3>
            
            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
              <p className="text-white/90 text-sm sm:text-base">
                {t('wizard.googleInstructions.description')}
              </p>
              
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-orange-300 font-medium mb-2 sm:mb-3">
                  {t('wizard.googleInstructions.important')}
                </p>
                <img 
                  src="/google-drive-permission.png" 
                  alt="Google Drive permission checkbox" 
                  className="w-full rounded-lg border border-white/20 shadow-lg"
                />
              </div>
              
              <p className="text-white/80 text-xs sm:text-sm">
                {t('wizard.googleInstructions.note')}
              </p>
            </div>
            
            <div className="flex gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setShowGoogleInstructions(false)}
                className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 bg-white/10 text-white rounded-lg sm:rounded-xl hover:bg-white/20 transition-colors font-medium text-sm sm:text-base"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={proceedWithGoogleConnection}
                className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-lg sm:rounded-xl hover:from-blue-600 hover:to-green-600 transition-colors font-medium shadow-lg text-sm sm:text-base"
              >
                {t('wizard.googleInstructions.continue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instagram Explanation Modal - Shows before redirecting to Instagram OAuth */}
      {showInstagramExplanationModal && (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm" style={{ touchAction: 'none' }}>
          <div className="bg-gray-900/95 backdrop-blur-xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-8 border border-white/20 mx-0 sm:mx-4 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-5">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/30 mb-4">
                <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </div>
              
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {t('dashboard.instagramExplanation.heading')}
              </h3>
              
              <p className="text-white/90 text-base sm:text-lg leading-relaxed">
                {t('dashboard.instagramExplanation.description')}
              </p>
            </div>

            {/* Permission Screen Preview */}
            <div className="mb-4">
              <p className="text-white/70 text-xs font-medium mb-2 text-center">
                {t('dashboard.instagramExplanation.permissionPreview')}
              </p>
              <div className="bg-white rounded-xl p-3 shadow-lg border border-gray-200">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-900 font-semibold text-xs">Vaulto</p>
                    <p className="text-gray-500 text-[10px]">wants to access your account</p>
                  </div>
                </div>
                <div className="space-y-1.5 mb-3">
                  <div className="flex items-center space-x-2 text-gray-700 text-xs">
                    <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                    <span>Access profile information</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-700 text-xs">
                    <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                    <span>Access media and content</span>
                  </div>
                </div>
                <div className="bg-blue-500 text-white text-center py-1.5 rounded-lg text-xs font-medium">
                  Allow
                </div>
                <p className="text-center text-gray-400 text-[10px] mt-1.5">
                  {t('dashboard.instagramExplanation.permissionNote')}
                </p>
              </div>
            </div>

            {/* Safety Badge */}
            <div className="mb-5 p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center justify-center space-x-2">
                <div className="flex items-center space-x-1 bg-green-500/20 px-2 py-1 rounded-full">
                  <CheckCircleIcon className="h-4 w-4 text-green-400" />
                  <span className="text-green-300 text-xs font-medium">{t('dashboard.instagramExplanation.readOnlyBadge')}</span>
                </div>
              </div>
              <p className="text-white/70 text-xs text-center mt-2">
                {t('dashboard.instagramExplanation.neverModify')}
              </p>
            </div>
            
            <div className="flex flex-col gap-2 sm:gap-3">
              <button
                type="button"
                onClick={proceedWithInstagramOAuth}
                className="w-full px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-colors font-semibold shadow-lg flex items-center justify-center text-sm sm:text-base"
              >
                {t('dashboard.instagramExplanation.continue')}
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </button>
              <button
                type="button"
                onClick={() => handleExitConfirm(() => setShowInstagramExplanationModal(false))}
                className="w-full px-4 py-2.5 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors font-medium text-sm sm:text-base"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingWizard;
